import { useMemo } from "react";
import { format } from "date-fns";
import { Download, Eye, Mail, Pencil, Trash2 } from "lucide-react";

import { DataTable } from "components/ui/DataTable";

const getAuthorName = (sickCert) => {
  if (sickCert.staffMember) {
    const firstName = sickCert.staffMember.firstName ?? "";
    const lastName = sickCert.staffMember.lastName ?? "";
    const name = `${firstName} ${lastName}`.trim();

    return name || sickCert.staffId || "—";
  }

  return sickCert.staffId || "—";
};

export const SickCertsTable = ({
  sickCerts,
  currentSickCertId,
  isReadOnly = false,
  onEdit,
  onDownload,
  onSendEmail,
  onDelete,
}) => {
  const columns = useMemo(
    () => [
      {
        id: "name",
        header: "Name",
        render: (sickCert) => sickCert.name || "Untitled",
      },
      {
        id: "dateRange",
        header: "Date Range",
        render: (sickCert) => {
          const from = sickCert.dateFrom
            ? format(new Date(sickCert.dateFrom), "dd/MM/yyyy")
            : "-";
          const to = sickCert.dateTo
            ? format(new Date(sickCert.dateTo), "dd/MM/yyyy")
            : "-";

          return `${from} - ${to}`;
        },
      },
      {
        id: "status",
        header: "Status",
        render: (sickCert) => sickCert.status || "DRAFT",
      },
      {
        id: "createdDate",
        header: "Created Date",
        render: (sickCert) =>
          sickCert.created
            ? format(new Date(sickCert.created), "dd/MM/yyyy")
            : "-",
      },
      {
        id: "createdBy",
        header: "Created By",
        render: (sickCert) => getAuthorName(sickCert),
      },
    ],
    []
  );

  const rowActions = useMemo(() => {
    const actions = [
      {
        label: isReadOnly ? "View sick cert" : "Edit sick cert",
        icon: isReadOnly ? Eye : Pencil,
        onClick: onEdit,
        primary: true,
        disabled: (row) => row.id === currentSickCertId,
      },
      {
        label: "Download PDF",
        icon: Download,
        onClick: onDownload,
        primary: true,
      },
    ];

    if (!isReadOnly) {
      actions.push(
        {
          label: "Send Email",
          icon: Mail,
          onClick: onSendEmail,
          primary: true,
        },
        {
          label: "Delete",
          icon: Trash2,
          onClick: onDelete,
          destructive: true,
        }
      );
    }

    return actions;
  }, [onEdit, onDownload, onSendEmail, onDelete, isReadOnly, currentSickCertId]);

  return (
    <div>
      <DataTable
        columns={columns}
        data={sickCerts}
        getRowId={(row) => row.id}
        rowActions={rowActions}
        rowActionsLabel="Sick cert actions"
        emptyTitle="No sick certs found"
        emptyText="There are no sick certs for this consultation."
        getRowClassName={(row) => (row.id === currentSickCertId ? "active" : "")}
        rowsPerPage={20}
        rowsOptions={[5, 10, 20, 50, "All"]}
      />
    </div>
  );
};
